import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { Link } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import palette from '../../../theme/palette'
import { awbLinkSx } from '../../common/sharedStyles'
import { useGetFreightPnlOrdersQuery } from '../../redux/freightPnlApiSlice'
import { exportErrorMessage, exportFreightOrdersCsv } from './freightPnlUtils'

const PRICED_COLS = [
  { key: 'awbNumber', label: 'AWB' },
  { key: 'orderReferenceNumber', label: 'Reference' },
  { key: 'merchantName', label: 'Merchant' },
  { key: 'merchantZone', label: 'Merchant zone' },
  { key: 'courierName', label: 'Courier' },
  { key: 'prozoId', label: 'Prozo Id' },
  { key: 'courierZone', label: 'Courier zone' },
  { key: 'pickupPincode', label: 'Facility pin' },
  // shipmentDetail.item_weight is stored in grams (Control Tower's CSV ships the same raw number).
  { key: 'shipmentWeight', label: 'Weight g', num: true },
  { key: 'merchantPrice', label: 'Merchant ₹', num: true },
  { key: 'courierPrice', label: 'Courier ₹', num: true },
  { key: 'profit', label: 'Profit ₹', num: true, signed: true },
]

const UNPRICED_COLS = [
  { key: 'awbNumber', label: 'AWB' },
  { key: 'orderReferenceNumber', label: 'Reference' },
  { key: 'awbRegisteredDate', label: 'AWB date' },
  { key: 'merchantName', label: 'Merchant' },
  { key: 'merchantZone', label: 'Merchant zone' },
  { key: 'prozoId', label: 'Prozo Id' },
  { key: 'courierName', label: 'Courier' },
  { key: 'courierZone', label: 'Courier zone' },
  { key: 'shipmentWeight', label: 'Weight g', num: true },
  { key: 'invoiceValue', label: 'Invoice ₹', num: true },
  { key: 'merchantPrice', label: 'Merchant ₹', num: true, missing: true },
  { key: 'courierPrice', label: 'Courier ₹', num: true, missing: true },
  { key: 'pickupPincode', label: 'Pickup pin' },
  { key: 'dropPincode', label: 'Drop pin' },
]

/**
 * Order-level rows behind the summary — the mail's two CSV attachments, on screen.
 * priced=true → "Order level"; priced=false → "Rates not calculated".
 * The backend bounds this live read (≤7 days all merchants, ≤31 days for one merchant); a 400 shows as a hint.
 */
export default function OrdersTab({ scope, priced, total = 0, skip = false, onExported }) {
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [exporting, setExporting] = useState(false)

  // A new scope (dates / segment / merchant / loss-making set) starts from page 1 — otherwise a page kept from a
  // bigger scope requests rows past the end of the new one and shows an empty table.
  const scopeKey = JSON.stringify(scope)
  useEffect(() => { setPage(0) }, [scopeKey, priced])

  // The backend does not count (a count would re-scan the range on every page); `total` comes from the summary.
  const body = { ...scope, priced, page: page + 1, pageSize }
  const { data, isFetching, error } = useGetFreightPnlOrdersQuery(body, { skip })

  const cols = priced ? PRICED_COLS : UNPRICED_COLS
  const rows = data?.rows || []
  const backendMsg = error?.data?.message || error?.message
  // Rows are live from Mongo while `total` is the nightly rollup, so they can disagree (day not yet computed, courier
  // price backfilled since). Paginate open-ended: a full page means "there may be more", whatever the rollup says.
  const hasMore = rows.length === pageSize
  const shownFrom = page * pageSize + 1
  const shownTo = page * pageSize + rows.length

  const exportCsv = async () => {
    setExporting(true)
    try {
      await exportFreightOrdersCsv(scope, priced)
      if (onExported) onExported(priced) // the page shows the mail's "rates not calculated" statement after any download
    } catch (e) {
      // 409 = export slots busy on the server; 400 = range too wide. Blob errors carry no JSON body, so fall back to a hint.
      enqueueSnackbar(exportErrorMessage(e), { variant: 'error' })
    } finally {
      setExporting(false)
    }
  }

  const cell = (row, col) => {
    const v = row[col.key]
    if (col.missing && (v === null || v === undefined || v === '')) {
      return <span style={{ color: palette.error.dark, fontWeight: 600 }}>missing</span>
    }
    if (col.key === 'awbNumber' && v) {
      // Same target as the NDR/NPR tables: the order-detail page keyed by AWB.
      return <Box component={Link} to={`/order-detail/${encodeURIComponent(v)}`} sx={awbLinkSx}>{v}</Box>
    }
    if (col.signed && typeof v === 'number' && v < 0) {
      return <span style={{ color: palette.error.dark, fontWeight: 600 }}>{v}</span>
    }
    return v === null || v === undefined || v === '' ? '—' : String(v)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary', minWidth: 0 }}>
          {priced
            ? 'Every shipment inside the profit figures, with both prices. Rows are read live from orders; the tab count comes from the nightly rollup.'
            : 'Shipments excluded from every profit figure because a price could not be calculated at booking. Courier prices usually backfill within a day; merchant prices do not. Rows are live, so a backfilled shipment leaves this list before the next rollup.'}
        </Typography>
        {/* flexShrink 0: the intro text wraps instead of squeezing the button, whose centred content would spill the icon past its border. */}
        <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={exportCsv} disabled={exporting || Boolean(error)} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {exporting ? 'Preparing…' : `CSV · ${total.toLocaleString('en-IN')} rows`}
        </Button>
      </Box>

      {error && (
        <Alert severity='info' sx={{ mb: 1 }}>
          {backendMsg || 'Order-level data is limited to 7 days, or 31 days for a single merchant. Narrow the date range or pick one merchant.'}
        </Alert>
      )}

      {skip ? (
        <Typography variant='body2' sx={{ py: 4, textAlign: 'center', color: 'text.disabled' }}>No brands in scope</Typography>
      ) : isFetching ? (
        <Box sx={{ p: 2 }}>{[...Array(8)].map((_, i) => <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />)}</Box>
      ) : !error && (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ '& td, & th': { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } }}>
              <TableHead>
                <TableRow>
                  {cols.map(c => <TableCell key={c.key} align={c.num ? 'right' : 'left'} sx={{ bgcolor: palette.grey[200], fontWeight: 700, color: 'text.secondary' }}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.awbNumber || i} hover>
                    {cols.map(c => <TableCell key={c.key} align={c.num ? 'right' : 'left'}>{cell(r, c)}</TableCell>)}
                  </TableRow>
                ))}
                {!rows.length && <TableRow><TableCell colSpan={cols.length} align='center' sx={{ py: 4, color: 'text.disabled' }}>No orders in this scope</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div' count={-1} page={page} rowsPerPage={pageSize}
            onPageChange={(_, p) => setPage(p)}
            nextIconButtonProps={{ disabled: isFetching || !hasMore }}
            labelDisplayedRows={() => rows.length === 0
              ? '0 rows'
              : `${shownFrom.toLocaleString('en-IN')}–${shownTo.toLocaleString('en-IN')}${total ? ` of ~${total.toLocaleString('en-IN')} (per nightly rollup)` : ''}`}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[50, 100, 200]}
          />
        </>
      )}
    </Box>
  )
}
